import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from '../state/session.tsx';
import { useStoredPreference } from '../hooks/index.ts';
import { Button, classes } from './ui.tsx';
import { PLANS, RECOMMENDED_PLAN_ID, planRanksBelow } from '@shared/constants/index.ts';

/**
 * The one place in the app that argues for a plan nobody asked about.
 *
 * Everything else that mentions a tier is an answer to something the person
 * just tried to do — a gated page, a refused write — and says the minimum
 * that would have let them do it. This is the other kind: an unprompted
 * suggestion, on a screen that is working perfectly well without it. So it is
 * held to stricter rules than a notice:
 *
 *  - Only where the pitch is about the work on the screen. A nudge for
 *    texting on the reports page is an advertisement; the same nudge next to
 *    a quote that has sat unopened for a week is a suggestion.
 *  - Only to somebody who can act on it — the owner. Nobody else can change
 *    the plan, so for them it is an interruption with no button.
 *  - Only to a company below the tier being suggested, obviously.
 *  - Dismissible, and dismissed for good. The preference is per company and
 *    per nudge, so closing the one on the quotes list does not silence a
 *    different one somewhere else, and joining a different company does not
 *    inherit a decision made at the last one.
 *
 * The Worker knows nothing about any of this: a nudge is a suggestion, not a
 * gate, and it never stands between anybody and their work.
 */
export function UpgradeNudge({
  /** Distinguishes this nudge's dismissal from every other one's. */
  id,
  /** i18n key under `upgradeNudge.` for the body copy. */
  messageKey,
}: {
  id: string;
  messageKey: string;
}) {
  const { t } = useTranslation();
  const { user, company } = useSession();
  const planName = PLANS[RECOMMENDED_PLAN_ID].name;
  const [dismissed, setDismissed] = useStoredPreference(
    `upgrade-nudge:${company?.id ?? 'unknown'}:${id}`,
    ['open', 'dismissed'] as const,
    'open',
  );

  if (dismissed === 'dismissed') return null;
  if (user?.role !== 'owner') return null;
  if (!company || !planRanksBelow(company.effectivePlan, RECOMMENDED_PLAN_ID)) return null;

  return (
    <div
      className={classes(
        'mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3',
        'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)]',
      )}
    >
      <p className="text-sm text-[color:var(--color-brand-strong)]">
        <span className="font-semibold">{t('upgradeNudge.label', { plan: planName })}</span>{' '}
        {t(`upgradeNudge.${messageKey}`, { plan: planName })}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Link
          to="/settings#settings-billing"
          className="text-sm font-semibold text-[color:var(--color-brand)] underline"
        >
          {t('upgradeNudge.cta', { plan: planName })}
        </Link>
        <Button variant="ghost" onClick={() => setDismissed('dismissed')}>
          {t('common.dismiss')}
        </Button>
      </div>
    </div>
  );
}
